// Authentication Provider Component
import React, { createContext, useContext, useEffect, useState } from 'react'
import { authHelpers } from '../supabaseClient'
import { BACKEND_URL } from '../config'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)

  const [adminSession, setAdminSession] = useState(null)

  useEffect(() => {
    let active = true
    setAdminSession(null)
    const token = session?.access_token
    if (token) {
      fetch(`${BACKEND_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(response => response.ok ? response.json() : null)
        .then(result => {
          if (active && result?.user?.is_admin === true) setAdminSession(token)
        })
        .catch(() => { /* Admin UI fails closed; regular user access is unaffected. */ })
    }
    return () => { active = false }
  }, [session?.access_token])

  useEffect(() => {
    // Get initial session
    const getSession = async () => {
      const { data: { session } } = await authHelpers.getSession()
      setSession(session)
      setUser(session?.user || null)
      setLoading(false)
    }

    getSession()

    // Listen for auth changes
    const { data: { subscription } } = authHelpers.onAuthStateChange(
      async (event, session) => {
        setSession(session)
        setUser(session?.user || null)
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const signUp = async (email, password, userData) => {
    const { data, error } = await authHelpers.signUp(email, password, userData)
    return { data, error }
  }

  const signIn = async (email, password) => {
    const { data, error } = await authHelpers.signIn(email, password)
    return { data, error }
  }

  const signOut = async () => {
    const { error } = await authHelpers.signOut()
    return { error }
  }

  const value = {
    user,
    session,
    signUp,
    signIn,
    signOut,
    loading,
    isAdmin: Boolean(session?.access_token && adminSession === session.access_token)
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
